import { Task, User, TaskStatus } from './types';
import { roleHasPermission } from './permissions';

export class BadRequestException extends Error {
  statusCode: number;

  constructor(message: string) {
    super(message);
    this.name = 'BadRequestException';
    this.statusCode = 400;
  }
}

export type CanonicalStatus = 'TO_DO' | 'IN_PROGRESS' | 'IN_REVIEW' | 'COMPLETED' | 'BACKLOG' | 'OVERDUE';
export type Status = CanonicalStatus | TaskStatus | string;

export interface TransitionUser extends Partial<User> {
  id: string;
  workspaceRole?: string;
  projectRole?: string;
  role?: any;
}

export interface TransitionTask extends Partial<Task> {
  id: string;
  status: any;
  assigneeId?: string | null;
  assignee_id?: string | null;
  assignorId?: string | null;
  assignor_id?: string | null;
  creatorId?: string;
  creator_id?: string;
}

export interface TransitionOptions {
  requireReviewApproval?: boolean;
}

/**
 * Normalizes any task status (lowercase, uppercase, alias) to canonical uppercase format.
 */
export function normalizeStatus(status: string | undefined | null): CanonicalStatus {
  if (!status) return 'TO_DO';
  const s = status.toString().trim().toUpperCase();
  if (s === 'DONE' || s === 'COMPLETED') return 'COMPLETED';
  if (s === 'IN_REVIEW' || s === 'INREVIEW') return 'IN_REVIEW';
  if (s === 'IN_PROGRESS' || s === 'INPROGRESS') return 'IN_PROGRESS';
  if (s === 'TODO' || s === 'TO_DO') return 'TO_DO';
  if (s === 'BACKLOG') return 'BACKLOG';
  if (s === 'OVERDUE') return 'OVERDUE';
  return s as CanonicalStatus;
}

/**
 * Normalizes role string to canonical uppercase role.
 */
export function normalizeRole(role: string | undefined | null): string {
  if (!role) return '';
  const r = role.toString().trim().toUpperCase();
  if (r === 'LEAD') return 'TEAM_LEAD';
  return r;
}

/**
 * Reference implementation of Role-Based Access Control & Kanban Approval Engine.
 * 
 * Enforces:
 * 1. Viewers and Guests can never change task status
 * 2. Moving to COMPLETED is strictly locked to roles that may approve tasks and project leads
 * 3. Reopening a COMPLETED task is locked to Admin or Team Lead
 * 4. Rejecting from IN_REVIEW to IN_PROGRESS by a reviewer requires a comment
 * 5. Members/Assignees can transition between TO_DO, IN_PROGRESS, and IN_REVIEW
 */
export function canTransitionTask(
  user: TransitionUser,
  task: TransitionTask,
  targetStatus: Status,
  reason?: string,
  options?: TransitionOptions
): boolean {
  if (!user || !task) return false;

  const normProjectRole = normalizeRole(user?.projectRole || (user?.role === 'viewer' ? 'VIEWER' : user?.role === 'guest' ? 'GUEST' : undefined));
  const normWorkspaceRole = normalizeRole(user?.workspaceRole || (user?.role === 'admin' ? 'ADMIN' : undefined));

  // 1. Viewers and Guests can never change task status
  // (a viewer / guest role is read-only unless it was given "Edit & move tasks" on Roles & Access)
  const roleIsReadOnly = (user?.role === 'viewer' || user?.role === 'guest') && !roleHasPermission(user?.role, 'task.edit');
  if (normProjectRole === 'VIEWER' || normProjectRole === 'GUEST' || roleIsReadOnly) {
    return false;
  }

  const effectiveAssigneeId = task.assigneeId || task.assignee_id;

  // Which roles may complete / review tasks is set on the Roles & Access screen ("task.approve").
  // Someone who leads this project can still review it. Having created or assigned the task does not
  // make a member a reviewer: completing work is decided by the role, not by who made the task.
  const roleCanApprove = roleHasPermission(user?.role, 'task.approve');
  const isPrivilegedReviewer =
    normWorkspaceRole === 'ADMIN' ||
    user?.role === 'admin' ||
    normProjectRole === 'TEAM_LEAD' ||
    roleCanApprove;

  const normTarget = normalizeStatus(targetStatus);
  const normCurrent = normalizeStatus(task.status);

  // 2. Moving to COMPLETED is strictly locked to roles that may approve tasks (Roles & Access) and project leads
  if (normTarget === 'COMPLETED') {
    if (options?.requireReviewApproval === false) {
      return true;
    }
    return isPrivilegedReviewer;
  }

  // 3. Reopening a COMPLETED task is locked to Admin or Team Lead
  if (normCurrent === 'COMPLETED' && normTarget !== 'COMPLETED') {
    return normWorkspaceRole === 'ADMIN' || user?.role === 'admin' || normProjectRole === 'TEAM_LEAD' || roleCanApprove;
  }

  // 3b. Once a task is in review, only someone who may review it can take it back out (and a reviewer
  // sending it back must leave feedback, below). Everyone else cannot pull it back to In Progress or
  // To Do on their own, which would skip the review.
  if (normCurrent === 'IN_REVIEW' && normTarget !== 'IN_REVIEW' && normTarget !== 'COMPLETED' && !isPrivilegedReviewer) {
    return false;
  }

  // 4. Rejecting from IN_REVIEW to IN_PROGRESS by a reviewer requires a comment
  if (normCurrent === 'IN_REVIEW' && normTarget === 'IN_PROGRESS' && isPrivilegedReviewer && user?.id !== effectiveAssigneeId) {
    if (!reason || reason.trim().length === 0) {
      throw new BadRequestException('A rejection reason is required when sending a task back to In Progress.');
    }
    return true;
  }

  // 5. Members/Assignees can transition between TO_DO, IN_PROGRESS, and IN_REVIEW (including BACKLOG)
  if (['TO_DO', 'IN_PROGRESS', 'IN_REVIEW', 'BACKLOG'].includes(normTarget)) {
    return true;
  }

  return false;
}

/**
 * Non-throwing transition check returning boolean and error message.
 */
export function checkTaskTransition(
  user: TransitionUser,
  task: TransitionTask,
  targetStatus: Status,
  reason?: string,
  options?: TransitionOptions
): { allowed: boolean; error?: string } {
  try {
    if (!user) {
      return { allowed: false, error: 'Authentication required to perform this task transition.' };
    }
    const allowed = canTransitionTask(user, task, targetStatus, reason, options);
    if (!allowed) {
      const normTarget = normalizeStatus(targetStatus);
      const normCurrent = normalizeStatus(task?.status);

      if (user?.projectRole === 'VIEWER' || user?.role === 'viewer') {
        return { allowed: false, error: 'Viewers have read-only access and cannot change task status.' };
      }
      if (user?.projectRole === 'GUEST' || user?.role === 'guest') {
        return { allowed: false, error: 'Guests cannot change task status.' };
      }
      if (normTarget === 'COMPLETED') {
        return { allowed: false, error: 'Moving to Completed is limited to roles that may review tasks.' };
      }
      if (normCurrent === 'COMPLETED' && normTarget !== 'COMPLETED') {
        return { allowed: false, error: 'Reopening a completed task is limited to roles that may review tasks.' };
      }
      if (normCurrent === 'IN_REVIEW' && normTarget !== 'IN_REVIEW') {
        return {
          allowed: false,
          error: 'Only people who can review tasks may send a task back from In Review (with feedback). Ask a reviewer to do it.',
        };
      }
      return { allowed: false, error: 'You do not have permission to perform this task status transition.' };
    }
    return { allowed: true };
  } catch (err: any) {
    if (err instanceof BadRequestException) {
      return { allowed: false, error: err.message };
    }
    return { allowed: false, error: err?.message || 'Invalid status transition.' };
  }
}
